import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { setGlobalVariable } from "../shared";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import customLinkForLIONReource from "./customLinkForLIONReource";

function Assignments({
  assignmentsData,
  searchTermAssignments,
  setSearchTermAssignments,
  currentPageAssignments,
  setCurrentPageAssignments,
  rowsPerPageAssignments,
  sortOrderAssignments,
  setSortOrderAssignments,
  activeTab,
  baseUrl,
  lionResource,
}) {
  const [intValue, setIntValue] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [selectedBenchmark, setSelectedBenchmark] = useState("All"); // Default to "All"
  const all = require('../icons/ALL.png');
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const downArrow = require("../icons/downarrow.png");

  //downArrow
  useEffect(() => {
    const scrollContainer = document.querySelector(".main-content");
    if (!scrollContainer) return;

    const handleScroll = () => {
      const bottom =
        scrollContainer.scrollTop + scrollContainer.clientHeight >=
        scrollContainer.scrollHeight - 10;
      setShowScrollIndicator(!bottom);
    };

    scrollContainer.addEventListener("scroll", handleScroll);

    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(handleScroll);
    });

    resizeObserver.observe(scrollContainer);

    requestAnimationFrame(handleScroll);

    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
      resizeObserver.disconnect();
    };
  }, []);

  // HTML Popup states
  const [selectedHtml, setSelectedHtml] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // PDF Popup states
  const [selectedPdfUrl, setSelectedPdfUrl] = useState(null);
  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);

  // Decode HTML helper
  const decodeHtmlEntities = (text) => {
    const txt = document.createElement("textarea");
    txt.innerHTML = text;
    return txt.value;
  };

  useEffect(() => {
    const studentkey = sessionStorage.getItem("studentkey");
    const districtID = sessionStorage.getItem("districtID");
    const token = sessionStorage.getItem("authToken");
    const headers = {
      Authorization: `Bearer ${token}`, // Include the token in the Authorization header
      "Content-Type": "application/json", // Optional: Define the content type if needed
    };
    if (token != null) {
      axios
        .get(
          baseUrl +
          `Student/GetHelpPageId?studentkey=${studentkey}&districtId=${districtID}&url=${activeTab}`,
          { headers }
        )
        .then((response) => {
          const fetchedValue = response.data;
          setIntValue(fetchedValue);
          setGlobalVariable(fetchedValue);
        })
        .catch((error) => {
          console.error("Error fetching intValue:", error);
        });
    }
  }, [activeTab, baseUrl]);

  const jsonObj = useMemo(() => JSON.parse(lionResource), [lionResource]);
  const data = useMemo(() => jsonObj.lionResources || [], [jsonObj]);
  const teacherData = useMemo(() => jsonObj.assignment || [], [jsonObj]);

  // Date formatting helpers
  function _monthToNum(date) {
    if (!date) return null;
    let year, month, day;
    if (date.includes("T") || date.includes("-")) {
      const parts = date.split("T")[0].split("-");
      [year, month, day] = parts;
    } else if (date.includes("/")) {
      const parts = date.split("/");
      [month, day, year] = parts;
    }
    if (!year || !month || !day) return null;
    return Number(`${year}${month.padStart(2, "0")}${day.padStart(2, "0")}`);
  }

  function dateComparator(date1, date2) {
    const date1Number = _monthToNum(date1);
    const date2Number = _monthToNum(date2);
    if (date1Number === null && date2Number === null) return 0;
    if (date1Number === null) return -1;
    if (date2Number === null) return 1;
    return date1Number - date2Number;
  }

  function dateFormatter(params) {
    const dateAsString = params.value;
    if (!dateAsString) return "";
    const date = new Date(dateAsString);
    if (isNaN(date)) return dateAsString;
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  }

  // Teacher Assignments Columns
  const [teacherAsgmtColumnDefs] = useState([
    {
      headerName: "Assignment Title",
      field: "Title",
      filter: true,
      flex: 3,
      minWidth: 350,
      cellStyle: { textAlign: "start", alignItems: "center" },
      cellClass: "descWrap",

      //  Click to open PDF popup
      cellRenderer: (params) => {
        const title = params.value || "View PDF";
        const pdfLink = params.data?.PDF;

        if (!pdfLink) return <span>{title}</span>;

        return (
          <span
            style={{
              color: "#007bff",
              textDecoration: "underline",
              cursor: "pointer",
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation(); // prevent HTML popup
              setSelectedPdfUrl(pdfLink);
              setIsPdfModalOpen(true);
            }}
          >
            {title}
          </span>
        );
      },
    },
    {
      headerName: "Description",
      field: "Description",
      filter: true,
      flex: 3,
      minWidth: 370,
      cellStyle: { textAlign: "start", alignItems: "center" },
      cellClass: "descWrap",
    },
    {
      headerName: "HTML",
      flex: 2,
      minWidth: 180,
      cellStyle: { textAlign: "center", alignItems: "center" },
      cellClass: "descWrap",
      cellRenderer: (params) => {
        const htmlValue = params.data?.HTML;
        if (!htmlValue || htmlValue.trim() === "") return null;

        return (
          <span
            style={{
              color: "#007bff",
              textDecoration: "underline",
              cursor: "pointer",
            }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation(); // prevent row click
              const decoded = decodeHtmlEntities(htmlValue);
              setSelectedHtml(decoded);
              setIsModalOpen(true);
            }}
          >
            View Content
          </span>
        );
      },
    },
    {
      headerName: "Assigned Date",
      field: "DateAssigned",
      valueFormatter: dateFormatter,
      comparator: dateComparator,
      flex: 1,
      minWidth: 140,
      cellStyle: { textAlign: "center" },
    },
    {
      headerName: "Due Date",
      field: "DateDue",
      valueFormatter: dateFormatter,
      comparator: dateComparator,
      flex: 1,
      minWidth: 140,
      cellStyle: { textAlign: "center" },
    },
  ]);


  const [columnDefs] = useState([

    {
      headerName: "Media Source",
      field: "MediaSource",
      filter: true,
      filterParams: {
        buttons: ["reset", "cancel"],
        closeOnApply: true,
      },
      cellStyle: { textAlign: "center", alignItems: "center" },
      flex: 1,
      minWidth: 155,
    },

    {
      headerName: "Activity Title", field: "Title", filter: true,
      filterParams: {
        buttons: ["reset", "cancel"],
        closeOnApply: true,
      },
      cellRenderer: customLinkForLIONReource,
      cellRendererParams: {
        baseUrl,
      },
      flex: 3, minWidth: 350,
      cellStyle: { textAlign: "start", alignItems: "center" },
      cellClass: "descWrap"
    },

    {
      headerName: "Resource Type",
      field: "ResourceType",
      filter: true,
      filterParams: {
        buttons: ["reset", "cancel"],
        closeOnApply: true,
      },
      cellStyle: { textAlign: "center", alignItems: "center" },
      flex: 1,
      minWidth: 165,
    },
    {
      headerName: "Language",
      field: "Language",
      filter: true,
      filterParams: {
        buttons: ["reset", "cancel"],
        closeOnApply: true,
      },
      cellStyle: { textAlign: "center", alignItems: "center" },
      flex: 1,
      minWidth: 135,
    },


    {
      headerName: "TEKS", field: "skill", filter: true,
      filterParams: {
        buttons: ["reset", "cancel"],
        closeOnApply: true,
      },
      sort: "asc", flex: 1, minWidth: 120
    },
    {
      headerName: "TEKS Desc",
      field: "Description",
      filter: true,
      filterParams: {
        buttons: ["reset", "cancel"],
        closeOnApply: true,
      },
      flex: 3,
      minWidth: 370,

      cellStyle: { textAlign: "start", alignItems: "center" },
      cellClass: "descWrap"
    },
    {
      headerName: "Complete",
      field: "Complete",
      filter: true,
      filterParams: {
        buttons: ["reset", "cancel"],
        closeOnApply: true,
      },
      cellStyle: { textAlign: "center", alignItems: "center" },
      flex: 1,
      minWidth: 140,
    },
    {
      headerName: "Time Spent",
      field: "TimeSpent",
      filter: true,
      filterParams: {
        buttons: ["reset", "cancel"],
        closeOnApply: true,
      },
      cellStyle: { textAlign: "center", alignItems: "center" },
      flex: 1,
      minWidth: 140,
    },


    { headerName: "QuestionId", field: "QuestionId", hide: true },
    { headerName: "IbeReleaseId", field: "IbeReleaseId", hide: true },
    { headerName: "AssignedResourceKey", field: "AssignedResourceKey", hide: true },
    { headerName: "Product", field: "Product", hide: true },
  ]);

  useEffect(() => {
    if (selectedBenchmark === "All") {
      setFilteredData(data);
    } else {
      const filtered = data.filter((item) => item.Benchmark === selectedBenchmark);
      setFilteredData(filtered);
    }
  }, [selectedBenchmark, data]);

  const handleBenchmarkClick = (benchmark) => {
    setSelectedBenchmark(benchmark);
  };

  // const uniqueBenchmarks = useMemo(
  //   () => ["All", ...new Set(data.map((item) => item.Benchmark))],
  //   [data]
  // );

  const uniqueBenchmarks = useMemo(
    () => [
      { benchmark: "All", icon: "" }, // Add a default "All" item with no icon
      ...[
        ...new Set(
          data.map((item) => `${item.Benchmark}-${item.ColorIcon}`) // Create a unique combination of Benchmark and Icon
        ),
      ].map((benchmarkIcon) => {
        const [benchmark, icon] = benchmarkIcon.split("-"); // Split the combination into Benchmark and Icon
        return { benchmark, icon };
      }),
    ]
      .sort((a, b) => {
        if (a.benchmark === "All") return -1;
        if (b.benchmark === "All") return 1;
        return a.benchmark.localeCompare(b.benchmark);
      }),
    [data]
  );


  return (
    <>
      <div>
        {/*  BOTH GRIDS EMPTY */}
        {data.length === 0 && teacherData.length === 0 ? (
          <div style={{ textAlign: "center", marginTop: "10%" }}>
            <h3>No data available</h3>
          </div>
        ) : (
          <>
            {/*  STUDENT RESOURCES GRID */}
            {data.length > 0 && (
              <div>
                <h3>Assigned Resources</h3>
                {/* Render benchmark tabs */}
                <div style={{ marginBottom: "10px" }}>
                  {uniqueBenchmarks.map(({ benchmark, icon }) => (
                    <button
                      key={benchmark}
                      onClick={() => handleBenchmarkClick(benchmark)}
                      className={`subject-button ${selectedBenchmark === benchmark ? 'active' : ''}`}
                    >
                      {benchmark === "All" ? (
                        <img src={all} alt="All icon" className="subject-icon" />
                      ) : (
                        icon && <img src={icon} alt={`${benchmark} icon`} className="subject-icon" />
                      )}
                      {benchmark}
                    </button>
                  ))}
                </div>

                <div className="ag-theme-alpine" style={{ height: "auto", marginBottom: "35px" }}>
                  <AgGridReact
                    rowData={filteredData}
                    columnDefs={columnDefs}
                    domLayout="autoHeight"
                    suppressScrollOnNewData={true}
                  />
                </div>
              </div>
            )}

            {/* Divider only if BOTH grids have data */}
            {data.length > 0 && teacherData.length > 0 && (
              <hr
                style={{
                  border: "none",
                  height: "3px",
                  margin: "40px 0",
                  borderRadius: "2px",
                  background:
                    "linear-gradient(to right, transparent, #519B52 40%, #519B52 60%, transparent)",
                }}
              />
            )}

            {/* TEACHER ASSIGNMENTS GRID */}
            {teacherData.length > 0 && (
              <div>
                <h3>Teacher Assignments</h3>
                <div
                  className="ag-theme-alpine"
                  style={{ height: "auto", marginBottom: "35px" }}
                >
                  <AgGridReact
                    rowData={teacherData}
                    columnDefs={teacherAsgmtColumnDefs}
                    domLayout="autoHeight"
                    suppressScrollOnNewData={true}
                  />
                </div>
              </div>
            )}
          </>
        )}

        {/* HTML Content Popup */}
        {isModalOpen && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              backgroundColor: "rgba(0,0,0,0.6)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1000,
            }}
            onClick={() => setIsModalOpen(false)}
          >
            <div
              style={{
                background: "#fff",
                borderRadius: "10px",
                boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
                width: "85%",
                maxWidth: "900px",
                height: "85vh",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header Bar */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  backgroundColor: "#519B52",
                  color: "#fff",
                  padding: "10px 20px",
                  fontWeight: "bold",
                }}
              >
                <span>📝 HTML Content Preview</span>

                <button
                  onClick={() => setIsModalOpen(false)}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "#fff",
                    fontSize: "18px",
                    cursor: "pointer",
                  }}
                  title="Close"
                >
                  ✖
                </button>
              </div>

              {/* Content Body */}
              <div
                style={{
                  padding: "20px",
                  overflowY: "auto",
                  flexGrow: 1,
                  backgroundColor: "#fafafa",
                }}
                dangerouslySetInnerHTML={{ __html: selectedHtml }}
              />
            </div>
          </div>
        )}

        {/* PDF Preview Popup */}
        {isPdfModalOpen && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              backgroundColor: "rgba(0,0,0,0.6)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1000,
            }}
            onClick={() => setIsPdfModalOpen(false)}
          >
            <div
              style={{
                background: "#fff",
                borderRadius: "10px",
                boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
                width: "85%",
                maxWidth: "1000px",
                height: "85vh",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header Bar */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  backgroundColor: "#519B52",
                  color: "#fff",
                  padding: "10px 20px",
                  fontWeight: "bold",
                }}
              >
                <span>📄 PDF Preview</span>

                <div>
                  <a
                    href={selectedPdfUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: "#fff",
                      textDecoration: "none",
                      marginRight: "15px",
                      backgroundColor: "#457A23",
                      padding: "6px 12px",
                      borderRadius: "4px",
                      fontSize: "14px",
                    }}
                  >
                    🌐 Open in Browser
                  </a>

                  <button
                    onClick={() => setIsPdfModalOpen(false)}
                    style={{
                      background: "transparent",
                      border: "none",
                      color: "#fff",
                      fontSize: "18px",
                      cursor: "pointer",
                    }}
                    title="Close"
                  >
                    ✖
                  </button>
                </div>
              </div>

              {/* PDF Content */}
              <iframe
                src={selectedPdfUrl}
                title="PDF Preview"
                style={{
                  flexGrow: 1,
                  width: "100%",
                  border: "none",
                  backgroundColor: "#f5f5f5",
                }}
              />
            </div>
          </div>
        )}
      </div>

      {showScrollIndicator && (
        <div
          style={{
            position: "fixed",
            bottom: "20px",
            right: "20px",
            background: "#d0cfcf99",
            color: "white",
            borderRadius: "50%",
            width: "40px",
            height: "40px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "22px",
            zIndex: 9999,
            cursor: "pointer",
            animation: "bounce 1.5s infinite",
          }}
          onClick={() =>
            window.scrollBy({ top: 200, behavior: "smooth" })
          }
        >
          <img
            src={downArrow}
            alt="Scroll down"
            style={{ width: "22px", height: "22px" }}
          />
        </div>
      )}

    </>
  );
}

export default Assignments;